/**
 * Listing Management (Syndication): UI strings for the Standard Listings and
 * Promoted Listings sections, their modals, conditional content, and CSV export
 * headers. Built for RSX-5695 (iFraming French translations, Part 1).
 *
 * RSX-5750 (Part 3) added the `layouts`, `autoResponders`, and `integrations`
 * sub-namespaces (plus `common` and the extra `nav` keys) for the Layouts,
 * Auto Responders, and Integrations sections and their modals/conditional content.
 *
 * RSX-5751 (Part 4, final) added the `settings` sub-namespace (+ `nav.listingSettings`)
 * for the Listing Settings section — General settings, Property Overrides, the
 * super-admin Legacy Settings, and the per-partner syndication settings pages.
 *
 * Structure: single top-level `listingManagement` namespace. Keep values in the
 * same order in the En and Fr objects. French renders only inside the iframe.
 */


export const listingManagementEn = {
  listingManagement: {
    nav: {
      listingManagement: 'Listing Management',
      standardListings: 'Standard Listings',
      promotedListings: 'Promoted Listings',
      connections: 'Connections',
      connectionDetails: 'Details',
      connectionDetailsTitle: 'Connection Details',
      layouts: 'Layouts',
      autoResponders: 'Auto Responders',
      integrations: 'Integrations',
      listingSettings: 'Listing Settings',
    },

    standard: {
      filters: {
        syncingStates: 'Syncing States',
        connections: 'Connections',
        allPlaceholder: '[All]',
        manageConnections: 'Manage Connections',
        removeFilters: 'Remove ({count}) filter | Remove ({count}) filters',
      },
      cardForm: {
        propertyType: 'Property Type',
        listingScoreRange: 'Listing Score Range',
      },
      states: {
        activatedCount: 'Activated ({count})',
        deactivatedCount: 'Deactivated ({count})',
        unavailableCount: 'Unavailable ({count})',
      },
      grid: {
        availableUnits: 'Available Units',
        totalAvailableUnits: 'Total Available Units: {count}',
        listingScore: 'Listing Score',
        totalProperties: 'Total # of Properties',
        totalPropertiesTurnedOn: 'Total # of Properties Turned On',
        totalPropertiesSyncing: 'Total # of Properties Actively Syncing',
        noCity: 'No City',
      },
      tooltips: {
        propertyNameNav: 'Click on the Property Name to navigate to the Property Details page.',
        listingScore: 'Listing Score is a performance indicator that measures the content, quality and completeness of your rental listings.',
        visitLink: 'Visit Link',
        statusUnavailable: 'Syncing Unavailable - Properties will not be synced.',
        statusDeactivated: 'Syncing Turned Off - Properties are ready to sync.',
        statusActivated: 'Syncing Activated - Properties are syncing to the selected connections.',
        hasPromotedListing: 'This property has an active promoted listing.\nPlease manage the promoted \nlisting before switching off.',
        turnedOn: 'Turned on {date}',
        turnedOff: 'Turned off {date}',
        modifiedByImporter: ' - This connection was last modified by an importer.',
      },
      toast: {
        propertiesActivated: '{count} properties activated for {partner}!',
        propertiesDeactivated: '{count} properties deactivated for {partner}!',
        propertyActivated: '{name} activated for {partner}!',
        propertyDeactivated: '{name} deactivated for {partner}!',
      },
      changesSummary: {
        property: 'Property',
        city: 'City',
        connection: 'Connection',
        change: 'Change',
        changeName: 'Change: Syndication',
      },
      warnings: {
        propertyDisabledHidden: 'Property is set to disabled/hidden',
        propertyNoUnits: 'Property has no available units',
        propertyMissingRent: 'Property has missing rent fields for all units',
      },
      modal: {
        filtersTitle: 'Filters',
      },
      permissions: {
        notAvailableLocation: '{partner} is not available in this location.',
        notAvailableType: '{partner} is not available for {type} properties.',
      },
    },

    standardModals: {
      syncWarning: {
        title: 'Property Not Actively Syndicating',
        notSyndicatedReasons: 'This property will not actively be syndicated for the following reasons:',
        autoSyndicationInfo: 'Syndication will be done automatically once the specified criteria is met when this toggle is turned on.',
        okay: 'Okay',
      },
      syncStateInfo: {
        title: 'Listing Syndication States',
        okay: 'Okay',
        activated: {
          heading: 'Syncing Activated',
          intro: 'Properties are syncing to those Rental Listing Site(s) that have the listing syndication toggle turned on',
          mostCases: 'In most cases, newly enabled listings will start appearing online by the next day',
          someCases: 'In some cases, please wait 24-48 hours for the Rental Listings to appear online:',
          reasonConnected: 'if the Rental Listing Site has been connected to your Listing Syndication within the last 24 hours',
          reasonInformed: 'if you have been informed by Rentsync or the Rental Listing Site of the current status of your connection',
        },
        turnedOff: {
          heading: 'Syncing Turned Off',
          intro: 'This property is ready to sync:',
          turnOnToggle: 'turn on the toggle for the Rental Listing Site(s) that you want to list the property on',
          dontForgetSave: 'don’t forget to save',
        },
        unavailable: {
          heading: 'Syncing Unavailable',
          intro: 'Properties will not be synced if the property:',
          disabled: 'is set to disabled',
          noUnits: 'has no units listed as available',
          rentMissing: 'rent fields are missing for all units (at least one enabled unit is required to have the rent field filled out in order to sync)',
        },
      },
    },

    manage: {
      title: 'Manage Promoted Listing',
      requestTransfer: 'Request Transfer',
      requestCancellation: 'Request Cancellation',
      transferIntro: 'Transfer your promoted listing to a different property to avoid losing bundle discounts and reclaim opportunities to reach a wider audience of renters.',
      selectProperty: 'Select Property',
      requestedTransferDate: 'Requested Transfer Date',
      cancelWarning: 'You will lose the volume discount pricing if you cancel this promoted listing. Consider transferring it to another property instead.',
      cancelIntro: 'In order to cancel your promoted listing subscription, your Rentsync account manager will need to approve the request.',
      reasonForCancellation: 'Reason for Cancellation',
      specifyReason: 'Specify Reason',
      requestedCancellationDate: 'Requested Cancellation Date',
      businessDaysNotice: 'Please allow up to 2 business days for a Rentsync account manager to approve your request.',
      disclaimerTransfer: 'I agree to transfer this promoted listing subscription and its billing implications to the selected property.',
      disclaimerCancel: 'I agree to cancel this promoted listing subscription and its billing implications for all my properties.',
      cancelSubscription: 'Cancel Subscription',
      request: 'Request',
      transfer: 'Transfer',
      reasons: {
        noVacancy: 'No Vacancy',
        lowVacancies: 'Low number of vacancies',
        propertyMgmtChange: 'Ownership/management of property has changed',
        budgetConstraints: 'Budget constraints',
        notEnoughLeads: 'Not receiving as many leads as expected',
        notInterested: 'Not interested in promoted listings',
        differentPropertyNeedsLeads: 'Different property needs more leads',
        other: 'Other',
      },
    },

    pricing: {
      bundleToSave: 'Bundle To Save - {partner} {type}',
      thresholdRow: '{range} {type} Listings',
      costRow: '{cost} / property / month',
      upgrade: 'Upgrade',
    },

    promoted: {
      banner: {
        title: 'New Promoted Connection',
        message: 'You can now add promoted listings for {connections}.',
      },
      // Catalogue PDF URLs. Locale-specific: swap the Fr values for French PDFs
      // once marketing supplies them (Fr currently falls back to the English asset).
      catalogue: {
        promotedListingsCatalogue: 'https://rs-assets.rentsync.com/platform-assets/promoted-listings-catalogue.pdf',
        infinityNetworkOverview: 'https://rentsync.s3.amazonaws.com/partner_logos/v2_Rentalsca_Infinity_Network_Overview.pdf',
      },
      toolbar: {
        removeFilters: 'Remove ({count}) Filter | Remove ({count}) Filters',
        manageConnections: 'Manage Connections',
        accessOnly: 'Promoted Listings Access Only',
        addPromotedListing: 'Add Promoted Listing',
        help: 'Help',
      },
      emptyState: {
        noData: 'Get started by clicking the \'Add Promoted Listing\' button to help your listings reach a broader audience!',
      },
      actions: {
        upgrade: 'Upgrade',
      },
      columns: {
        upgradeTier: 'Upgrade Tier',
        bundle: 'Bundle',
        manage: 'Manage',
      },
      csv: {
        property: 'Property',
        city: 'City',
        rentalListingSite: 'Rental Listing Site',
        tier: 'Tier',
        startDate: 'Start Date',
        expiryDate: 'Expiry Date',
        status: 'Status',
      },
      tier: {
        promotedListings: 'Promoted Listings',
      },
      status: {
        active: 'Active',
        inactive: 'Inactive',
        requested: 'Requested',
        expired: 'Expired',
        scheduled: 'Scheduled',
      },
      tooltips: {
        bundleToSave: 'Bundle To Save',
        savingMost: 'You\'re saving the most money!',
        infinityApex: 'This property has an Infinity Apex subscription',
      },
      statusTooltip: {
        underReview: 'Your request is under review. Please allow up to 2 business days to process your request or contact your Rentsync account manager for more information.',
        expired: 'Expired {date}',
        activeIn: 'Active in {count} days',
      },
      manageTooltip: {
        cannotManageInApp: 'This Promoted Listing cannot be managed in-app',
        cannotManageUntil: 'This Promoted Listing cannot be managed until {date}',
        transferApproved: 'Transfer approved',
        transferRequested: 'Transfer requested',
        cancellationApproved: 'Cancellation approved',
        cancellationRequested: 'Cancellation requested',
        upgradeApproved: 'Upgrade approved',
        upgradeRequested: 'Upgrade requested',
      },
      toast: {
        generatingExport: 'Generating promoted listings export',
        errorExportTitle: 'Error Generating Export',
        contactSupport: 'Please contact support for assistance.',
        requestSentTitle: 'Promoted Listings Request Sent',
        approvalDelay: 'Please allow up to 2 business days for a Rentsync account manager to approve your request.',
        transferSentTitle: 'Transfer Request Sent',
        errorTransferTitle: 'Error Requesting Transfer',
        cancellationSentTitle: 'Cancellation Request Sent',
        errorCancellationTitle: 'Error Requesting Cancellation',
        upgradeSubmitted: 'Your upgrade request has been submitted.',
        errorUpgradeTitle: 'Error Requesting Upgrade',
      },
    },

    promotedCard: {
      headerPromoted: 'Increase your lead volume by up to 5x with Promoted Listings*',
      headerInfinity: 'Increase your exposure to the max with the Rentals.ca Infinity Network',
      bodyPromotedLead: 'Looking for ways to increase your lead volume?',
      bodyPromoted: 'By upgrading from standard to promoted listings, you’ll increase listing visibility, get priority placement in your area, and ultimately increase your daily lead volume on that network.',
      bodyInfinityPre: 'Whether you’re filling a lease-up or ensuring you’re at occupancy, you’ll feel confident you’re',
      bodyInfinityBold: 'reaching the most renters. Period.',
      bodyInfinityPost: 'Benefit from higher placement by default when compared with non-bundled listings. Or get even higher placement when you add on an Infinity promoted listing package to your unique properties, giving you 2x, 4x, 6x, or Max the exposure levels!',
      termsApply: '*Terms & Conditions apply',
      viewCatalogue: 'View Catalogue',
    },

    addWizard: {
      common: {
        modalTitle: 'Add Promoted Listing(s)',
        next: 'Next',
        minimumDurationNotice: 'Please note that every promoted listing has a minimum duration of 60 days from the start date (with the ability to transfer available after 30 days when 10+ promoted listings are active). If a promoted listing is transferred or cancelled during this period, you will be charged for the full minimum term.',
        businessDaysNotice: 'Please allow up to 2 business days for promoted listings to be added to your account.',
        contactManagerHelp: 'Please click on the Help button or contact your Rentsync account manager if you have any questions or need more information.',
      },
      disclaimer: {
        contactManagerInquire: 'Please click on the Inquire button or contact your Rentsync account manager if you have any questions or need more information.',
      },
      selection: {
        bundleBannerTitle: 'Bundle to Save',
        bundleBannerMessage: 'Add more promoted listings to get discounted rates. Learn more <a href=\'https://rs-assets.rentsync.com/platform-assets/promoted-listings-catalogue.pdf\' target=\'_blank\'>here</a>. <i>*Terms and conditions apply</i>',
        labelProperty: 'Property',
        labelRls: 'Rental Listing Site (RLS)',
        labelTier: 'Tier',
        selectProperty: 'Select Property',
        selectRls: 'Select RLS',
        selectTier: 'Select Tier',
        noUnitsTitle: 'No Available Units',
        noUnitsMessage: 'To create your promoted listing, please select a property with available units.',
      },
      summary: {
        headerProperty: 'Property',
        headerRls: 'Rental Listing Site (RLS)',
        headerTier: 'Tier',
        headerMonthlyFee: 'Monthly Fee (Before Tax)',
        tbd: 'TBD',
        priceChangeNotice: 'Price change may apply based on volume discounts.',
        infinityMaxNotice: 'Your Rentsync Account Manager will reach out to you shortly regarding the <b>Infinity Max</b> request.',
        consentLabel: 'I agree that I have verified all the information on the page and authorize Rentsync to add these new billing charges to my account effective immediately.',
        back: 'Back',
      },
      confirmation: {
        title: 'Your Request is Processing',
        okay: 'Okay',
        thankYou: 'Thank you for adding promoted listing(s). We have received your request and are currently processing it.',
      },
    },

    upgrade: {
      title: 'Upgrade Connection: {partner}',
      propertyName: 'Property: {name}',
      numberOfListings: '# of Listings',
      pricingPerMonth: 'Pricing (per month)',
      listingsPerPricing: '# of Listings / Pricing (per month)',
      leadTraffic: 'Lead Traffic',
      upgrade: 'Upgrade',
      tier: 'Tier',
      listingsRange: '{range} Listings',
      // eslint-disable-next-line no-template-curly-in-string -- literal `$` + vue-i18n `{price}` param, not a template literal
      pricePerProperty: '${price} / property',
      current: 'Current',
      selected: 'Selected',
      pricesFromBundle: '*prices start from bundle to save',
      termsApply: 'As per contract, terms and conditions apply.',
      allowBusinessDays: 'Please allow up to 2 business days to see changes on the platform.',
      contactManager: 'Contact your Rentsync account manager or click on the \'Inquire\' button if you have questions or need more information.',
      minimumDuration: 'Please note that every promoted listing has a minimum duration of 60 days from the start date. If a promoted listing is cancelled during this period, you will be charged for the full minimum term.',
      reviewProperty: 'Property',
      rls: 'Rental Listing Site (RLS)',
      monthlyFee: 'Monthly Fee (Before Tax)',
      total: 'Total',
      pricingChangeVolume: 'Pricing change may apply based on volumne discounts.',
      allowBusinessDaysAdd: 'Please allow up to 2 business days for promoted listings to be added to your account.',
      contactHelp: 'Please click on the Help button or contact your Rentsync account manager if you have any questions or need more information',
      consent: 'I agree that I have verified all the information on the page and authorize Rentsync to add these new billing charges to my account effective immediately.',
      back: 'Back',
      requestUpgrade: 'Request Upgrade',
    },

    promotedFilters: {
      title: 'Filters',
      rentalListingSites: 'Rental Listing Sites',
      expiryDate: 'Expiry Date',
      date: 'Date',
      customDateRange: 'Custom Date Range Filter: {type}',
      dateRangeError: '* <strong>To</strong> date cannot be before <strong>From</strong> date!',
      inactive: 'Inactive',
      thisMonth: 'This Month',
      lastMonth: 'Last Month',
    },

    common: {
      contactManagerToEnable: 'Contact your Rentsync account manager to enable this feature.',
    },

    layouts: {
      rentalListingSite: 'Rental Listing Site',
      property: 'Property',
      selectService: 'Select a Service',
      infinityNetworkFeed: 'Rentals.ca Infinity Network - {name}',
      previewYourListings: 'Preview Your Listings',
      previewTooltip: 'Please select a Rental Listing Site and Property to preview layout.',
      viewPreview: 'View Preview',
      customizeYourLayouts: 'Customize Your Layouts',
      bannerTitle: 'Listing Layouts allow you to control the main description of your listings',
      bannerMessage: 'You can use the dropdown below to choose which Rental Listing Site(s) have customized layouts or edit the \'Default\' layout to customize all listings.',
      saveLayout: 'Save Layout',
      searchContentBlocks: 'Search content blocks',
      dropzoneHint: 'Drag & Drop elements from left \'Content Block\' panel to the space above',
      defaultLayout: 'Default',
      updatePreview: 'Update Preview',
      previewDisclaimer: 'This is only a static preview of your listing and functionality is limited',
      selectCustomField: 'Select Custom Field',
      fallbackValue: 'Fallback Value',
      fallbackPlaceholder: 'Add a fallback value',
      removeBlock: {
        title: 'Remove Content Block',
        text: 'Are you sure you want to remove this content block? This action cannot be undone.',
      },
      confirmCancelChanges: 'Are you sure you want to cancel these changes?',
      toast: {
        settingsSaved: 'Syndication Settings saved!',
        errorSavingSettings: 'Error Saving Settings!',
        clientIdMissing: 'Client ID is missing',
        failedSaveContentBlocks: 'Failed to save content blocks: {error}',
        unknownError: 'Unknown error',
      },
      contentBlock: {
        title: 'Title',
        titlePlaceholder: 'Add a title for the content',
        conditionalOptions: 'Conditional Options',
        showIf: 'Show this content block if:',
        addCondition: 'Add Condition',
        translate: 'Translate',
        fr: 'FR',
        comparison: {
          equal: 'is equal to',
          greater: 'is greater than',
          less: 'is less than',
          notEqual: 'is not equal to',
          isIn: 'is in',
          notIn: 'is not in',
          empty: 'empty',
          notEmpty: 'not empty',
        },
        dataGroups: {
          propertyData: 'Property Data',
          unitData: 'Unit Data',
        },
        dataFields: {
          propertyId: 'Property ID',
          propertyName: 'Property Name',
          cityName: 'City Name',
          provinceCode: 'Province Code',
          promotions: 'Promotions',
          propertyOverview: 'Property Overview',
          buildingFeatures: 'Building Features',
          locationDetails: 'Location Details',
          suiteDetails: 'Suite Details',
          unitId: 'Unit ID',
          unitTypeName: 'Unit Type Name',
          unitDescription: 'Unit Description',
        },
        customFieldGroups: {
          global: 'Global Custom Field',
          buildings: 'Buildings Custom Field',
          suiteTypes: 'Suite Types Custom Field',
        },
      },
      amenities: {
        display: 'Display',
        classifications: 'Classifications',
        categories: 'Categories',
        selectAmenities: 'Select Amenities',
        selectCategory: 'Select Category',
        selectClassification: 'Select Classification',
        displayOptions: {
          all: 'All Amenities',
          category: 'Category',
          classification: 'Classification',
          selected: 'Selected Amenities',
        },
      },
      promotions: {
        hideTitle: 'Hide Title',
        hideDetails: 'Hide Details',
      },
      unitsInfo: {
        hideType: 'Hide Type',
        hideDescription: 'Hide Description',
      },
      utilities: {
        displayStyle: 'Display style',
        styles: {
          list: 'List',
          comma: 'Comma Separated',
          inclusive: 'Inclusive',
          inclusiveText: 'Inclusive Text',
        },
      },
    },

    autoResponders: {
      activateEmail: 'Activate Auto Response Email',
      intro: 'Enable Auto Responders to send an immediate email reply to prospective tenants when they submit a lead.',
      noPermission: 'You do not have permission to modify auto responder settings',
      enableLogging: 'Enable Logging',
      property: 'Property',
      email: 'Email',
      emailPlaceholder: 'useremail@mail.com',
      sendTestEmail: 'Send Test Email',
      onOff: 'On/Off',
      service: 'Service',
      name: 'Name',
      fromLabel: '\'From\'',
      replyToLabel: '\'Reply To\'',
      nameTooltip: 'Choose which name will be shown as the sender of the Auto Responder',
      emailTooltip: 'A generic no reply email will be used to prevent email bounce-backs. Please contact support with any questions',
      customFromEmailBanner: 'Please create a support ticket for managing custom \'From\' email settings.',
      interpolationSupport: 'Subject and message support <strong>interpolation</strong>.',
      seeExamples: 'Click here to see examples',
      subjectLabel: 'Auto Response Subject',
      messageLabel: 'Auto Response Message',
      togglePropertiesTitle: 'Toggle Properties On/Off',
      toggleRlsTitle: 'Toggle Rental Listing Site On/Off',
      selectPropertyOption: 'Select a property',
      tabs: {
        template: 'Template',
        propertySettings: 'Property Settings',
        rlsSettings: 'Rental Listing Site Settings',
      },
      preview: {
        title: 'Auto Response Preview',
      },
      template: {
        title: 'Auto Responder Template',
        exampleButton: 'Auto Responder Example',
      },
      fromNameOptions: {
        propertyName: 'Property Name',
        companyName: 'Company Name',
        customName: 'Custom Name',
      },
      fromEmailLabels: {
        propertyEmail: 'Property Email',
        genericEmail: 'Generic Email (no-reply)',
        custom: 'Custom',
      },
      replyToOptions: {
        propertyEmail: 'Property Email',
        custom: 'Custom',
        none: 'None (use From email)',
      },
      settingsOptions: {
        all: 'All',
        custom: 'Custom',
      },
      interpolationModal: {
        title: 'Interpolation',
        assistanceContactAt: 'If you need assistance using interpolation or setting up your auto responder, please contact support at:',
        contactSupport: 'Contact Support',
        assistanceSuffix: 'if you need assistance using interpolation or setting up your auto responder.',
        explainer: 'This refers to adding dynamic data from your form into otherwise static text.',
        howto: 'All interpolation is done by adding a "key" in double square brackets within your text.',
        example: 'Example:',
        exampleValue: '"Hi, [[full_name]]!"',
        backToList: '< Back to Previous List',
        allPropertyFields: 'All Property Fields',
        showAllFields: 'Show All {label} Fields',
      },
      guide: {
        title: 'INTERPOLATION GUIDE',
        heading: 'Auto Responder Example',
        subject: 'Subject:',
        exampleSubject: 'Thank you for your rental inquiry at [[property]] in [[city]]',
        emailBody: 'Email Body:',
        line1: 'Hi [[first_name]]',
        line2: 'The team at [[property]] is reviewing your submission and will be in touch with you soon.',
        line3: 'If you\'d like to fill out an application form you can find an online form on www.yourwebsitehere.com',
        line4: 'If you want to reach out to us in the meantime you can give us a call at [[property_phone]] or visit our rental offices @[[property_address]].',
        line5: 'Thanks for your interest and we hope to welcome you home soon!',
      },
      toast: {
        error: 'Error',
        alert: 'Alert',
        success: 'Success',
        invalidEmail: 'Please enter a valid email address!',
        selectProperty: 'Please select a property!',
        invalidReplyTo: 'Custom \'Reply To\' email is invalid',
        customNameRequired: 'Custom name is required',
        subjectRequired: 'English Auto Response Subject is required',
        messageRequired: 'English Auto Response Message is required',
        savedSuccess: 'Successfully Saved.',
        testEmailSent: 'Test Email Sent Successfully. Please check to confirm.',
      },
    },

    integrations: {
      title: 'Integrations',
      subtitle: 'Set up Rentsync to deliver a webhook when leads are received from advertising sources.',
      sendLeadsSwitch: 'Send Rental Listing Site leads via webhook',
      noPermission: 'You do not have permission to modify webhook settings',
      authKey: 'Auth Key',
      webhookName: 'Webhook Name',
      endpoint: 'Endpoint',
      endpointHelp: 'Register your webhook to be able to capture Rental Listing Site leads automatically in your system of choice',
      propertiesHelp: 'Filter webhook delivery to specific properties',
      allProperties: 'All Properties',
      validateHelp: 'Have you completed the setup? Use this tool to validate whether your webhook setup is functioning properly.',
      generateTestLead: 'Generate a Test Lead',
      sendTestLead: 'Send Test Lead',
      leadSent: 'Lead sent!',
      testLeadWaitIframe: 'If you don\'t get a test lead for the property selected in the next 15 minutes, please contact Support at:',
      testLeadWait: 'If you don\'t get a test lead for the property selected in the next 15 minutes,',
      contactSupport: 'please contact Support',
      debugOnly: 'This button should be used for debugging purposes only.',
      enableLogging: 'Enable Logging (Developers Only)',
      toast: {
        validationError: 'Validation Error',
        fieldsRequired: 'Fields should be filled.',
        settingsTitle: 'Integration Settings',
        savedSuccess: 'Successfully Saved.',
        sentSuccess: 'Successfully Sent.',
      },
    },

    settings: {
      index: {
        general: 'General',
        unsavedChanges: 'You have unsaved changes.',
        settingsSaved: 'Syndication Settings saved!',
        errorSaving: 'Error Saving Client Settings!',
      },
      routes: {
        generalTitle: 'General Settings',
        syndicationTitle: 'Syndication Settings',
        settingsTitle: 'Settings',
        updateConnectionPrefs: 'Update Connection preferences',
        configureSpecific: 'Configure specific settings for your active Rental Listing Sites',
      },
      common: {
        listingContent: 'Listing Content',
        unitAvailability: 'Unit Availablility',
        chooseUnitAvailability: 'Choose setting for unit availability.',
        onlyAvailableUnits: 'Only Available Units',
        allUnits: 'All Units',
        buildingNameOverride: 'Building Name Override',
        superAdmin: 'Super Admin',
        pleaseNote: 'Please Note: ',
        billingNotice: 'This decision will affect how many listings appear on {site} which could affect your billing.',
        yes: 'Yes',
        no: 'No',
        select: 'Select',
        notAllowed: 'Not Allowed',
        allowed: 'Allowed',
        siteId: 'Site ID',
        omitBuildingDesc: 'Omit Building Description',
        omitBuildingDescHelp: 'Only when unit description is present can this occur',
        petFriendlyDefault: 'Pet Friendly Default Value',
        petFriendlyHelp: 'Can be "Yes" or "No". If no pet policy is found for the property, this will be the value that is sent to {target}.',
        smokingDefault: 'Smoking / Non-Smoking Default',
        smokingHelp: 'Can be "Allowed" or "Not Allowed". If no smoking amenity is checked for the property, this will be the value that is sent to {target}.',
        brokerageFeedDefault: 'Brokerage Feed Default',
        brokerageFeedHelp: 'Can be "No (keep in MITs)", "All Properties" or "All Single Family Homes". If no brokerage feed is found for the property, this will be the value that is sent to {target}.',
        brokerageKeepInMits: 'No (keep in MITs)',
        brokerageAllProperties: 'All Properties',
        brokerageAllSingleFamily: 'All Single Family Homes',
        multiMessaging: 'Multi Messaging',
        exclude: 'Exclude',
        multiMessagingHelp: 'Selecting the above checkbox will exclude your properties from Multi Messaging on Zumper listings.',
        digitalLeadConcierge: 'Digital Lead Concierge',
        optIn: 'Opt In',
        digitalLeadConciergeHelp: 'Selecting the above checkbox will include your properties in the Digital Lead Concierge feature on Zumper listings.',
      },
      general: {
        autoOnToggle: 'Rental Listing Sites Auto-On Toggle',
        autoOnHelp: 'Enable the Auto-On feature for Rental Listing Sites.',
        enableTrials: 'Enable Trials/Free Offers',
        enableTrialsHelp1: 'Enable new free connections and new trials that would benefit properties in your portfolio.',
        enableTrialsHelp2: 'You will still have control over which properties are enabled and disabled at any time.',
        propertyAvailability: 'Property Availability',
        propertyAvailabilityHelp: 'Choose the default filter to control which properties are shown in the Syndication section.',
        availabilityAll: 'All Properties',
        availabilityAvailable: 'Only Available Properties',
        availabilityNotAvailable: 'Not Available Properties',
        urlTracking: 'URL Tracking',
        urlTrackingHelp: 'Enabled Google Analytics Campaign URL Builder for every Rental Listing Site in Syndication.',
        trafficSource: 'Traffic Source:',
        trafficSourceValue: 'Syndication',
        medium: 'Medium',
        campaignName: 'Campaign Name',
        companyDescription: 'Company Description',
        companyDescriptionHelp: 'Provide a description/about paragraph that will be used for Rental Listing Sites.',
        serviceOrder: 'Service Order',
        serviceOrderHelp: 'Choose the order of how your Rental Listing Sites are displayed.',
        propertyOverrides: 'Property Overrides',
        legacySettings: 'Legacy Settings (Super Users Only)',
      },
      propertyOverrides: {
        intro: 'Override company listing elements with property listing elements for their respective rental listings.',
        globalIndividualHelp: 'Enable all properties to use the same <strong>global</strong> override settings. This will apply to properties that have no <strong>individual</strong> override settings defined.',
        globalFields: 'Global Property Override Fields',
        individualHelp: 'Select <strong>individual</strong> property settings. If <strong>individual</strong> property settings differ from <strong>global</strong> settings, the <strong>individual</strong> property settings will be applied.',
        searchPlaceholder: 'Search Properties...',
        overrideTooltip: 'This property has settings that override global settings',
        takeEffect: 'Property Override settings take effect on these enabled connections: {connections}',
        noEffect: 'Property Override settings will not take effect on any of your current connections.',
        fields: {
          property: 'Property',
          address: 'Address',
          email: 'Email',
          logo: 'Logo',
          name: 'Name',
          phone: 'Phone',
          site: 'Site',
        },
      },
      legacy: {
        intro: 'View and manage legacy settings that affect your rental listings.',
        banner: 'Legacy settings may affect how your listings are displayed across various platforms.',
        filterByPartner: 'Filter by Partner:',
        allPartners: 'All Partners',
        removeFilter: 'Remove Filter',
        addSetting: 'Add Setting',
        editSetting: 'Edit Setting',
        partner: 'Partner:',
        cannotChangePartner: 'Cannot change partner for existing settings.',
        settingName: 'Setting Name:',
        cannotChangeFieldKey: 'Cannot change field_key for existing settings.',
        selectPartnerFirst: 'Please select a partner first.',
        noSettingsForPartner: 'No settings available for this partner.',
        value: 'Value:',
        columns: {
          partner: 'Partner',
          fieldKey: 'Field Key',
          value: 'Value',
          description: 'Description',
          edit: 'Edit',
          delete: 'Delete',
        },
        toast: {
          error: 'Error',
          clientNotSet: 'Current client is not set.',
          fetchFailed: 'Failed to fetch legacy settings',
          noDescription: 'No description available.',
          idMissing: 'Setting ID is missing!',
          cannotDelete: 'This setting cannot be deleted because it is referenced by Ad Layouts. Please remove those content blocks first.',
          cannotDeleteTitle: 'Cannot Delete Setting!',
          deleteTitle: 'Delete Setting',
          deleteConfirm: 'Are you sure you want to delete the setting "{fieldKey}" for partner {partner}? This action cannot be undone.',
          deleted: 'Setting deleted successfully!',
          cannotDeleteDev: 'This Setting Cannot Be Deleted. Contact Development!',
          updated: 'Setting updated successfully!',
          updateFailed: 'Failed to update setting!',
          created: 'Setting created successfully!',
          createFailed: 'Failed to create setting!',
        },
      },
      dynamicControl: {
        jsonHelp: 'Enter valid JSON format',
        currentFormat: 'Current format: {format}',
        invalidJson: 'Invalid JSON format',
      },
      adCreation: {
        title: 'Listing Creation',
        groupUnits: 'Group Units',
        chooseHowGenerated: 'Choose the how ads are generated.',
        byBeds: 'By Number of Beds',
        byBedsBaths: 'By Number of Beds & Baths',
        byBedsBathsDen: 'By Beds, Baths & Den',
        byUnitName: 'By Unit Name',
        showAllUnits: 'Show All Units',
      },
      adTitle: {
        title: 'Listing Title',
        livePreview: 'Live Preview',
        titleFormat: 'Title Format',
        insertHelp: 'You can insert the following bold characters to use data specific to the property:',
        formatOption1: '[Building Name] - [Unit Name] [Building Type] for Rent',
        formatOption2: '[Unit Name] [Building Type] for Rent - [Building Address]',
        formatOption3: '[Building Type] for Rent: [Unit Name] - [Building Name]',
        formatCustom: 'Custom (Advanced)',
        numBedrooms: 'Number of bedrooms',
        numBathrooms: 'Number of bathrooms',
        optBuildingName: 'The name of the building',
        optOneLiner: 'One liner used to promote property',
        optAddress: 'The address of the building',
        optCityName: 'City Name',
        optProvinceName: 'Province Name',
        optUnitTypeName: 'Unit Type Name',
        optBuildingType: 'Building Type',
        optNeighbourhood: 'Neighbourhood',
      },
      tours: {
        title: 'Tour Settings',
        enableTours: 'Enable Tours',
        help: 'Tour URLs can be toggled on or off on your listings. This will only affect properties with active tours.',
      },
      partners: {
        kijiji: {
          contactNote: 'Contact Kijiji if you are unsure how many listings you recieve in your package.',
          showAmenities: 'Show Amenities',
          showUtilities: 'Show Utilities',
          petPolicyDefault: 'Pet Policy Default',
          smokingDefault: 'Smoking Default',
          salesforceId: 'Salesforce ID',
          salesforceHelp: 'Unique Identifier to connect your Rentsync account with Kijiji for syndication.',
          oneList: 'One List',
          categoryList: 'Category List',
          hidePetFriendly: 'Hide Pet Friendly Output',
          selectDefault: 'Select a Default',
          outdoorsOnly: 'Outdoors Only',
        },
        rentBoard: {
          siteIdHelp: 'Unique Identifier to connect your Rentsync account with Rent Board for syndication.',
          contactRentBoard: 'Contact Rent Board',
          receivePackage: 'if you are unsure how many listings you receive in your package.',
        },
        rentalsInfinity: {
          banner: 'These settings will only apply to all eligible Rental Listing Sites in the Rentals.ca Infinity Network.',
          toursRequest: 'Tours Request',
          toursLite: 'Tours Lite',
          toursLiteHelp: 'Enable basic prospect tour requests on eligible Rentals Listing Sites across your entire porfolio',
          siteIdHelp: 'Unique Identifier to connect your Rentsync account for syndication, with all eligible Rental Listing Sites in the Rentals.ca Infinity Network.',
        },
        gottarent: {
          displayingPrice: 'Displaying Price',
          choosePriceDisplay: 'Choose to show prices or show \'please call\' in the suites table.',
          showPrices: 'Show Prices',
          showPleaseCall: 'Show \'please call\'',
        },
        usedCa: {
          userAccountId: 'User Account ID',
          userAccountHelp: 'User Account ID is used to associate ads to a Used.ca Account.',
        },
      },
    },
  },
} as const;

export const listingManagementFr = {
  listingManagement: {
    nav: {
      listingManagement: 'Gestion des annonces',
      standardListings: 'Annonces standards',
      promotedListings: 'Annonces mises en avant',
      connections: 'Connexions',
      connectionDetails: 'Détails',
      connectionDetailsTitle: 'Détails de la connexion',
      layouts: 'Mises en page',
      autoResponders: 'Répondeurs automatiques',
      integrations: 'Intégrations',
      listingSettings: 'Paramètres d\'annonces',
    },

    standard: {
      filters: {
        syncingStates: 'États de synchronisation',
        connections: 'Connexions',
        allPlaceholder: '[Tous]',
        manageConnections: 'Gérer les connexions',
        removeFilters: 'Supprimer ({count}) filtre | Supprimer ({count}) filtres',
      },
      cardForm: {
        propertyType: 'Type de propriété',
        listingScoreRange: 'Plage de score de l\'annonce',
      },
      states: {
        activatedCount: 'Activé ({count})',
        deactivatedCount: 'Désactivé ({count})',
        unavailableCount: 'Indisponible ({count})',
      },
      grid: {
        availableUnits: 'Logements disponibles',
        totalAvailableUnits: 'Nombre total de logements disponibles : {count}',
        listingScore: 'Score de l\'annonce',
        totalProperties: 'Nombre total de propriétés',
        totalPropertiesTurnedOn: 'Nombre total de propriétés activées',
        totalPropertiesSyncing: 'Nombre total de propriétés en cours de synchronisation',
        noCity: 'Aucune ville',
      },
      tooltips: {
        propertyNameNav: 'Cliquez sur le nom de la propriété pour accéder à la page des détails de la propriété.',
        listingScore: 'Le score de l\'annonce est un indicateur de performance qui mesure le contenu, la qualité et l\'exhaustivité de vos annonces de location.',
        visitLink: 'Visiter le lien',
        statusUnavailable: 'Synchronisation indisponible - Les propriétés ne seront pas synchronisées.',
        statusDeactivated: 'Synchronisation désactivée - Les propriétés sont prêtes à être synchronisées.',
        statusActivated: 'Synchronisation activée - Les propriétés se synchronisent avec les connexions sélectionnées.',
        hasPromotedListing: 'Cette propriété possède une annonce mise en avant active.\nVeuillez gérer l\'annonce \nmise en avant avant de la désactiver.',
        turnedOn: 'Activé le {date}',
        turnedOff: 'Désactivé le {date}',
        modifiedByImporter: ' - Cette connexion a été modifiée pour la dernière fois par un importateur.',
      },
      toast: {
        propertiesActivated: '{count} propriétés activées pour {partner} !',
        propertiesDeactivated: '{count} propriétés désactivées pour {partner} !',
        propertyActivated: '{name} activée pour {partner} !',
        propertyDeactivated: '{name} désactivée pour {partner} !',
      },
      changesSummary: {
        property: 'Propriété',
        city: 'Ville',
        connection: 'Connexion',
        change: 'Modification',
        changeName: 'Modification : Synchronisation',
      },
      warnings: {
        propertyDisabledHidden: 'La propriété est définie comme désactivée/masquée',
        propertyNoUnits: 'La propriété n\'a aucun logement disponible',
        propertyMissingRent: 'La propriété a des champs de loyer manquants pour tous les logements',
      },
      modal: {
        filtersTitle: 'Filtres',
      },
      permissions: {
        notAvailableLocation: '{partner} n\'est pas disponible à cet emplacement.',
        notAvailableType: '{partner} n\'est pas disponible pour les propriétés de type {type}.',
      },
    },

    standardModals: {
      syncWarning: {
        title: 'Propriété non activement syndiquée',
        notSyndicatedReasons: 'Cette propriété ne sera pas activement syndiquée pour les raisons suivantes :',
        autoSyndicationInfo: 'La syndication sera effectuée automatiquement une fois les critères spécifiés remplis lorsque ce bouton est activé.',
        okay: 'D\'accord',
      },
      syncStateInfo: {
        title: 'États de syndication des annonces',
        okay: 'D\'accord',
        activated: {
          heading: 'Synchronisation activée',
          intro: 'Les propriétés sont synchronisées vers les sites d\'annonces locatives dont le bouton de syndication des annonces est activé',
          mostCases: 'Dans la plupart des cas, les nouvelles annonces activées commenceront à apparaître en ligne dès le lendemain',
          someCases: 'Dans certains cas, veuillez patienter de 24 à 48 heures pour que les annonces locatives apparaissent en ligne :',
          reasonConnected: 'si le site d\'annonces locatives a été connecté à votre syndication des annonces au cours des dernières 24 heures',
          reasonInformed: 'si Rentsync ou le site d\'annonces locatives vous a informé de l\'état actuel de votre connexion',
        },
        turnedOff: {
          heading: 'Synchronisation désactivée',
          intro: 'Cette propriété est prête à être synchronisée :',
          turnOnToggle: 'activez le bouton pour les sites d\'annonces locatives sur lesquels vous souhaitez publier la propriété',
          dontForgetSave: 'n\'oubliez pas d\'enregistrer',
        },
        unavailable: {
          heading: 'Synchronisation non disponible',
          intro: 'Les propriétés ne seront pas synchronisées si la propriété :',
          disabled: 'est définie comme désactivée',
          noUnits: 'n\'a aucune unité répertoriée comme disponible',
          rentMissing: 'les champs de loyer sont manquants pour toutes les unités (au moins une unité activée doit avoir le champ de loyer rempli pour pouvoir synchroniser)',
        },
      },
    },

    manage: {
      title: 'Gérer l\'annonce mise en avant',
      requestTransfer: 'Demander le transfert',
      requestCancellation: 'Demander l\'annulation',
      transferIntro: 'Transférez votre annonce mise en avant vers une autre propriété pour éviter de perdre les remises groupées et retrouver des occasions d\'atteindre un public plus large de locataires.',
      selectProperty: 'Sélectionner une propriété',
      requestedTransferDate: 'Date de transfert demandée',
      cancelWarning: 'Vous perdrez la tarification à remise sur volume si vous annulez cette annonce mise en avant. Envisagez plutôt de la transférer vers une autre propriété.',
      cancelIntro: 'Afin d\'annuler votre abonnement à l\'annonce mise en avant, votre gestionnaire de compte Rentsync devra approuver la demande.',
      reasonForCancellation: 'Raison de l\'annulation',
      specifyReason: 'Préciser la raison',
      requestedCancellationDate: 'Date d\'annulation demandée',
      businessDaysNotice: 'Veuillez prévoir jusqu\'à 2 jours ouvrables pour qu\'un gestionnaire de compte Rentsync approuve votre demande.',
      disclaimerTransfer: 'J\'accepte de transférer cet abonnement à l\'annonce mise en avant et ses implications de facturation vers la propriété sélectionnée.',
      disclaimerCancel: 'J\'accepte d\'annuler cet abonnement à l\'annonce mise en avant et ses implications de facturation pour toutes mes propriétés.',
      cancelSubscription: 'Annuler l\'abonnement',
      request: 'Demander',
      transfer: 'Transférer',
      reasons: {
        noVacancy: 'Aucune vacance',
        lowVacancies: 'Faible nombre de vacances',
        propertyMgmtChange: 'La propriété ou sa gestion a changé',
        budgetConstraints: 'Contraintes budgétaires',
        notEnoughLeads: 'Pas autant de prospects que prévu',
        notInterested: 'Pas intéressé par les annonces mises en avant',
        differentPropertyNeedsLeads: 'Une autre propriété a besoin de plus de prospects',
        other: 'Autre',
      },
    },

    pricing: {
      bundleToSave: 'Forfait pour économiser - {partner} {type}',
      thresholdRow: '{range} annonces {type}',
      costRow: '{cost} / propriété / mois',
      upgrade: 'Améliorer',
    },

    promoted: {
      banner: {
        title: 'Nouvelle connexion mise en avant',
        message: 'Vous pouvez maintenant ajouter des annonces mises en avant pour {connections}.',
      },
      // URL du catalogue PDF. Propre à la langue : remplacer les valeurs Fr par les PDF
      // français une fois fournis par le marketing (Fr utilise l'actif anglais par défaut).
      catalogue: {
        promotedListingsCatalogue: 'https://rs-assets.rentsync.com/platform-assets/2026_07_08_Promoted_Listing_Catalogue_fr.pdf',
        infinityNetworkOverview: 'https://rs-assets.rentsync.com/platform-assets/2026_07_08_Infinity_Promoted_Listing_Catalogue_fr.pdf',
      },
      toolbar: {
        removeFilters: 'Supprimer ({count}) filtre | Supprimer ({count}) filtres',
        manageConnections: 'Gérer les connexions',
        accessOnly: 'Accès aux annonces mises en avant uniquement',
        addPromotedListing: 'Ajouter une annonce mise en avant',
        help: 'Aide',
      },
      emptyState: {
        noData: 'Commencez par cliquer sur le bouton « Ajouter une annonce mise en avant » pour aider vos annonces à atteindre un public plus large !',
      },
      actions: {
        upgrade: 'Améliorer',
      },
      columns: {
        upgradeTier: 'Améliorer le niveau',
        bundle: 'Forfait',
        manage: 'Gérer',
      },
      csv: {
        property: 'Propriété',
        city: 'Ville',
        rentalListingSite: 'Site d\'annonces locatives',
        tier: 'Niveau',
        startDate: 'Date de début',
        expiryDate: 'Date d\'expiration',
        status: 'Statut',
      },
      tier: {
        promotedListings: 'Annonces mises en avant',
      },
      status: {
        active: 'Actif',
        inactive: 'Inactif',
        requested: 'Demandé',
        expired: 'Expiré',
        scheduled: 'Planifié',
      },
      tooltips: {
        bundleToSave: 'Forfait pour économiser',
        savingMost: 'Vous économisez le plus d\'argent !',
        infinityApex: 'Cette propriété possède un abonnement Infinity Apex',
      },
      statusTooltip: {
        underReview: 'Votre demande est en cours d\'examen. Veuillez prévoir jusqu\'à 2 jours ouvrables pour le traitement de votre demande ou communiquez avec votre gestionnaire de compte Rentsync pour plus d\'informations.',
        expired: 'Expiré le {date}',
        activeIn: 'Actif dans {count} jours',
      },
      manageTooltip: {
        cannotManageInApp: 'Cette annonce mise en avant ne peut pas être gérée dans l\'application',
        cannotManageUntil: 'Cette annonce mise en avant ne peut pas être gérée avant le {date}',
        transferApproved: 'Transfert approuvé',
        transferRequested: 'Transfert demandé',
        cancellationApproved: 'Annulation approuvée',
        cancellationRequested: 'Annulation demandée',
        upgradeApproved: 'Amélioration approuvée',
        upgradeRequested: 'Amélioration demandée',
      },
      toast: {
        generatingExport: 'Génération de l\'export des annonces mises en avant',
        errorExportTitle: 'Erreur lors de la génération de l\'export',
        contactSupport: 'Veuillez communiquer avec le soutien technique pour obtenir de l\'aide.',
        requestSentTitle: 'Demande d\'annonces mises en avant envoyée',
        approvalDelay: 'Veuillez prévoir jusqu\'à 2 jours ouvrables pour qu\'un gestionnaire de compte Rentsync approuve votre demande.',
        transferSentTitle: 'Demande de transfert envoyée',
        errorTransferTitle: 'Erreur lors de la demande de transfert',
        cancellationSentTitle: 'Demande d\'annulation envoyée',
        errorCancellationTitle: 'Erreur lors de la demande d\'annulation',
        upgradeSubmitted: 'Votre demande d\'amélioration a été soumise.',
        errorUpgradeTitle: 'Erreur lors de la demande d\'amélioration',
      },
    },

    promotedCard: {
      headerPromoted: 'Augmentez votre volume de prospects jusqu\'à 5x avec les Annonces mises en avant*',
      headerInfinity: 'Augmentez votre visibilité au maximum avec le Réseau Infinity de Rentals.ca',
      bodyPromotedLead: 'Vous cherchez des moyens d\'augmenter votre volume de prospects ?',
      bodyPromoted: 'En passant des annonces standards aux annonces mises en avant, vous augmenterez la visibilité de vos annonces, obtiendrez un placement prioritaire dans votre région et augmenterez à terme votre volume quotidien de prospects sur ce réseau.',
      bodyInfinityPre: 'Que vous remplissiez une nouvelle mise en location ou que vous assuriez votre taux d\'occupation, vous aurez la certitude de',
      bodyInfinityBold: 'toucher le plus grand nombre de locataires. Point final.',
      bodyInfinityPost: 'Bénéficiez d\'un meilleur placement par défaut par rapport aux annonces non groupées. Ou obtenez un placement encore plus élevé en ajoutant un forfait d\'annonces mises en avant Infinity à vos propriétés uniques, vous offrant 2x, 4x, 6x ou Max les niveaux de visibilité !',
      termsApply: '*Des conditions générales s\'appliquent',
      viewCatalogue: 'Voir le catalogue',
    },

    addWizard: {
      common: {
        modalTitle: 'Ajouter une/des annonce(s) mise(s) en avant',
        next: 'Suivant',
        minimumDurationNotice: 'Veuillez noter que chaque annonce mise en avant a une durée minimale de 60 jours à compter de la date de début (avec la possibilité de transfert après 30 jours lorsque plus de 10 annonces mises en avant sont actives). Si une annonce mise en avant est transférée ou annulée durant cette période, vous serez facturé pour la durée minimale complète.',
        businessDaysNotice: 'Veuillez prévoir jusqu\'à 2 jours ouvrables pour que les annonces mises en avant soient ajoutées à votre compte.',
        contactManagerHelp: 'Veuillez cliquer sur le bouton Aide ou contacter votre gestionnaire de compte Rentsync si vous avez des questions ou besoin de plus d\'informations.',
      },
      disclaimer: {
        contactManagerInquire: 'Veuillez cliquer sur le bouton Demande de renseignements ou contacter votre gestionnaire de compte Rentsync si vous avez des questions ou besoin de plus d\'informations.',
      },
      selection: {
        bundleBannerTitle: 'Forfait pour économiser',
        bundleBannerMessage: 'Ajoutez plus d\'annonces mises en avant pour obtenir des tarifs réduits. En savoir plus <a href=\'https://rs-assets.rentsync.com/platform-assets/2026_07_08_Promoted_Listing_Catalogue_fr.pdf\' target=\'_blank\'>ici</a>. <i>*Des conditions générales s\'appliquent</i>',
        labelProperty: 'Propriété',
        labelRls: 'Site d\'annonces locatives',
        labelTier: 'Niveau',
        selectProperty: 'Sélectionner une propriété',
        selectRls: 'Sélectionner un site d\'annonces locatives',
        selectTier: 'Sélectionner un niveau',
        noUnitsTitle: 'Aucun logement disponible',
        noUnitsMessage: 'Pour créer votre annonce mise en avant, veuillez sélectionner une propriété avec des logements disponibles.',
      },
      summary: {
        headerProperty: 'Propriété',
        headerRls: 'Site d\'annonces locatives',
        headerTier: 'Niveau',
        headerMonthlyFee: 'Frais mensuels (avant taxes)',
        tbd: 'À déterminer',
        priceChangeNotice: 'Un changement de prix peut s\'appliquer en fonction des remises sur volume.',
        infinityMaxNotice: 'Votre gestionnaire de compte Rentsync vous contactera sous peu concernant la demande <b>Infinity Max</b>.',
        consentLabel: 'Je confirme avoir vérifié toutes les informations sur la page et j\'autorise Rentsync à ajouter ces nouveaux frais de facturation à mon compte avec effet immédiat.',
        back: 'Retour',
      },
      confirmation: {
        title: 'Votre demande est en cours de traitement',
        okay: 'D\'accord',
        thankYou: 'Merci d\'avoir ajouté une/des annonce(s) mise(s) en avant. Nous avons reçu votre demande et la traitons actuellement.',
      },
    },

    upgrade: {
      title: 'Améliorer la connexion : {partner}',
      propertyName: 'Propriété : {name}',
      numberOfListings: 'Nombre d\'annonces',
      pricingPerMonth: 'Tarification (par mois)',
      listingsPerPricing: 'Nombre d\'annonces / Tarification (par mois)',
      leadTraffic: 'Trafic de prospects',
      upgrade: 'Amélioration',
      tier: 'Niveau',
      listingsRange: '{range} annonces',
      pricePerProperty: '{price} $ / propriété',
      current: 'Actuel',
      selected: 'Sélectionné',
      pricesFromBundle: '*les prix commencent à partir du forfait pour économiser',
      termsApply: 'Conformément au contrat, les modalités et conditions s\'appliquent.',
      allowBusinessDays: 'Veuillez prévoir jusqu\'à 2 jours ouvrables pour voir les changements sur la plateforme.',
      contactManager: 'Communiquez avec votre gestionnaire de compte Rentsync ou cliquez sur le bouton « Se renseigner » si vous avez des questions ou besoin de plus d\'informations.',
      minimumDuration: 'Veuillez noter que chaque annonce mise en avant a une durée minimale de 60 jours à compter de la date de début. Si une annonce mise en avant est annulée pendant cette période, vous serez facturé pour la totalité de la durée minimale.',
      reviewProperty: 'Propriété',
      rls: 'Site d\'annonces locatives (RLS)',
      monthlyFee: 'Frais mensuels (avant taxes)',
      total: 'Total',
      pricingChangeVolume: 'Des changements de prix peuvent s\'appliquer en fonction des remises sur volume.',
      allowBusinessDaysAdd: 'Veuillez prévoir jusqu\'à 2 jours ouvrables pour que les annonces mises en avant soient ajoutées à votre compte.',
      contactHelp: 'Veuillez cliquer sur le bouton Aide ou communiquer avec votre gestionnaire de compte Rentsync si vous avez des questions ou besoin de plus d\'informations',
      consent: 'Je conviens avoir vérifié toutes les informations sur la page et j\'autorise Rentsync à ajouter ces nouveaux frais de facturation à mon compte avec effet immédiat.',
      back: 'Retour',
      requestUpgrade: 'Demander l\'amélioration',
    },

    promotedFilters: {
      title: 'Filtres',
      rentalListingSites: 'Sites d\'annonces locatives',
      expiryDate: 'Date d\'expiration',
      date: 'Date',
      customDateRange: 'Filtre de plage de dates personnalisée : {type}',
      dateRangeError: '* La date <strong>À</strong> ne peut pas être antérieure à la date <strong>De</strong> !',
      inactive: 'Inactif',
      thisMonth: 'Ce mois-ci',
      lastMonth: 'Le mois dernier',
    },

    common: {
      contactManagerToEnable: 'Communiquez avec votre gestionnaire de compte Rentsync pour activer cette fonctionnalité.',
    },

    layouts: {
      rentalListingSite: 'Site d\'annonces locatives',
      property: 'Propriété',
      selectService: 'Sélectionner un service',
      infinityNetworkFeed: 'Rentals.ca Infinity Network - {name}',
      previewYourListings: 'Aperçu de vos annonces',
      previewTooltip: 'Veuillez sélectionner un site d\'annonces locatives et une propriété pour prévisualiser la mise en page.',
      viewPreview: 'Voir l\'aperçu',
      customizeYourLayouts: 'Personnalisez vos mises en page',
      bannerTitle: 'Les mises en page d\'annonces vous permettent de contrôler la description principale de vos annonces',
      bannerMessage: 'Vous pouvez utiliser le menu déroulant ci-dessous pour choisir quels sites d\'annonces locatives ont des mises en page personnalisées, ou modifier la mise en page « Par défaut » pour personnaliser toutes les annonces.',
      saveLayout: 'Enregistrer la mise en page',
      searchContentBlocks: 'Rechercher des blocs de contenu',
      dropzoneHint: 'Glissez-déposez les éléments du panneau « Bloc de contenu » à gauche vers l\'espace ci-dessus',
      defaultLayout: 'Par défaut',
      updatePreview: 'Mettre à jour l\'aperçu',
      previewDisclaimer: 'Ceci n\'est qu\'un aperçu statique de votre annonce et les fonctionnalités sont limitées',
      selectCustomField: 'Sélectionner un champ personnalisé',
      fallbackValue: 'Valeur de repli',
      fallbackPlaceholder: 'Ajouter une valeur de repli',
      removeBlock: {
        title: 'Supprimer le bloc de contenu',
        text: 'Êtes-vous sûr de vouloir supprimer ce bloc de contenu ? Cette action est irréversible.',
      },
      confirmCancelChanges: 'Êtes-vous sûr de vouloir annuler ces modifications ?',
      toast: {
        settingsSaved: 'Paramètres de syndication enregistrés !',
        errorSavingSettings: 'Erreur lors de l\'enregistrement des paramètres !',
        clientIdMissing: 'L\'identifiant du client est manquant',
        failedSaveContentBlocks: 'Échec de l\'enregistrement des blocs de contenu : {error}',
        unknownError: 'Erreur inconnue',
      },
      contentBlock: {
        title: 'Titre',
        titlePlaceholder: 'Ajouter un titre pour le contenu',
        conditionalOptions: 'Options conditionnelles',
        showIf: 'Afficher ce bloc de contenu si :',
        addCondition: 'Ajouter une condition',
        translate: 'Traduire',
        fr: 'FR',
        comparison: {
          equal: 'est égal à',
          greater: 'est supérieur à',
          less: 'est inférieur à',
          notEqual: 'n\'est pas égal à',
          isIn: 'est dans',
          notIn: 'n\'est pas dans',
          empty: 'vide',
          notEmpty: 'non vide',
        },
        dataGroups: {
          propertyData: 'Données de la propriété',
          unitData: 'Données du logement',
        },
        dataFields: {
          propertyId: 'ID de la propriété',
          propertyName: 'Nom de la propriété',
          cityName: 'Nom de la ville',
          provinceCode: 'Code de province',
          promotions: 'Promotions',
          propertyOverview: 'Aperçu de la propriété',
          buildingFeatures: 'Caractéristiques du bâtiment',
          locationDetails: 'Détails de l\'emplacement',
          suiteDetails: 'Détails de la suite',
          unitId: 'ID du logement',
          unitTypeName: 'Nom du type de logement',
          unitDescription: 'Description du logement',
        },
        customFieldGroups: {
          global: 'Champ personnalisé global',
          buildings: 'Champ personnalisé des bâtiments',
          suiteTypes: 'Champ personnalisé des types de suite',
        },
      },
      amenities: {
        display: 'Affichage',
        classifications: 'Classifications',
        categories: 'Catégories',
        selectAmenities: 'Sélectionner des commodités',
        selectCategory: 'Sélectionner une catégorie',
        selectClassification: 'Sélectionner une classification',
        displayOptions: {
          all: 'Toutes les commodités',
          category: 'Catégorie',
          classification: 'Classification',
          selected: 'Commodités sélectionnées',
        },
      },
      promotions: {
        hideTitle: 'Masquer le titre',
        hideDetails: 'Masquer les détails',
      },
      unitsInfo: {
        hideType: 'Masquer le type',
        hideDescription: 'Masquer la description',
      },
      utilities: {
        displayStyle: 'Style d\'affichage',
        styles: {
          list: 'Liste',
          comma: 'Séparé par des virgules',
          inclusive: 'Inclus',
          inclusiveText: 'Texte inclus',
        },
      },
    },

    autoResponders: {
      activateEmail: 'Activer le courriel de réponse automatique',
      intro: 'Activez les répondeurs automatiques pour envoyer une réponse immédiate par courriel aux locataires potentiels lorsqu\'ils soumettent un prospect.',
      noPermission: 'Vous n\'avez pas la permission de modifier les paramètres du répondeur automatique',
      enableLogging: 'Activer la journalisation',
      property: 'Propriété',
      email: 'Courriel',
      emailPlaceholder: 'useremail@mail.com',
      sendTestEmail: 'Envoyer un courriel de test',
      onOff: 'Activé/Désactivé',
      service: 'Service',
      name: 'Nom',
      fromLabel: '« De »',
      replyToLabel: '« Répondre à »',
      nameTooltip: 'Choisissez quel nom sera affiché comme expéditeur du répondeur automatique',
      emailTooltip: 'Un courriel générique sans réponse sera utilisé pour éviter les retours de courriel. Veuillez communiquer avec le soutien pour toute question',
      customFromEmailBanner: 'Veuillez créer un billet de soutien pour gérer les paramètres personnalisés du courriel « De ».',
      interpolationSupport: 'Le sujet et le message prennent en charge l\'<strong>interpolation</strong>.',
      seeExamples: 'Cliquez ici pour voir des exemples',
      subjectLabel: 'Objet de la réponse automatique',
      messageLabel: 'Message de la réponse automatique',
      togglePropertiesTitle: 'Activer/Désactiver les propriétés',
      toggleRlsTitle: 'Activer/Désactiver le site d\'annonces locatives',
      selectPropertyOption: 'Sélectionner une propriété',
      tabs: {
        template: 'Modèle',
        propertySettings: 'Paramètres des propriétés',
        rlsSettings: 'Paramètres des sites d\'annonces locatives',
      },
      preview: {
        title: 'Aperçu de la réponse automatique',
      },
      template: {
        title: 'Modèle de répondeur automatique',
        exampleButton: 'Exemple de répondeur automatique',
      },
      fromNameOptions: {
        propertyName: 'Nom de la propriété',
        companyName: 'Nom de l\'entreprise',
        customName: 'Nom personnalisé',
      },
      fromEmailLabels: {
        propertyEmail: 'Courriel de la propriété',
        genericEmail: 'Courriel générique (sans réponse)',
        custom: 'Personnalisé',
      },
      replyToOptions: {
        propertyEmail: 'Courriel de la propriété',
        custom: 'Personnalisé',
        none: 'Aucun (utiliser le courriel De)',
      },
      settingsOptions: {
        all: 'Tous',
        custom: 'Personnalisé',
      },
      interpolationModal: {
        title: 'Interpolation',
        assistanceContactAt: 'Si vous avez besoin d\'aide pour utiliser l\'interpolation ou configurer votre répondeur automatique, veuillez communiquer avec le soutien à :',
        contactSupport: 'Communiquer avec le soutien',
        assistanceSuffix: 'si vous avez besoin d\'aide pour utiliser l\'interpolation ou configurer votre répondeur automatique.',
        explainer: 'Cela consiste à ajouter des données dynamiques de votre formulaire dans un texte autrement statique.',
        howto: 'Toute interpolation se fait en ajoutant une « clé » entre doubles crochets dans votre texte.',
        example: 'Exemple :',
        exampleValue: '« Bonjour, [[full_name]] ! »',
        backToList: '< Retour à la liste précédente',
        allPropertyFields: 'Tous les champs de la propriété',
        showAllFields: 'Afficher tous les champs {label}',
      },
      guide: {
        title: 'GUIDE D\'INTERPOLATION',
        heading: 'Exemple de répondeur automatique',
        subject: 'Objet :',
        exampleSubject: 'Merci pour votre demande de location à [[property]] à [[city]]',
        emailBody: 'Corps du courriel :',
        line1: 'Bonjour [[first_name]]',
        line2: 'L\'équipe de [[property]] examine votre soumission et communiquera avec vous sous peu.',
        line3: 'Si vous souhaitez remplir un formulaire de demande, vous trouverez un formulaire en ligne sur www.yourwebsitehere.com',
        line4: 'Si vous souhaitez nous joindre entre-temps, vous pouvez nous appeler au [[property_phone]] ou visiter nos bureaux de location au [[property_address]].',
        line5: 'Merci de votre intérêt et nous espérons vous accueillir bientôt chez vous !',
      },
      toast: {
        error: 'Erreur',
        alert: 'Alerte',
        success: 'Succès',
        invalidEmail: 'Veuillez saisir une adresse courriel valide !',
        selectProperty: 'Veuillez sélectionner une propriété !',
        invalidReplyTo: 'Le courriel « Répondre à » personnalisé n\'est pas valide',
        customNameRequired: 'Le nom personnalisé est obligatoire',
        subjectRequired: 'L\'objet anglais de la réponse automatique est obligatoire',
        messageRequired: 'Le message anglais de la réponse automatique est obligatoire',
        savedSuccess: 'Enregistré avec succès.',
        testEmailSent: 'Courriel de test envoyé avec succès. Veuillez vérifier pour confirmer.',
      },
    },

    integrations: {
      title: 'Intégrations',
      subtitle: 'Configurez Rentsync pour livrer un webhook lorsque des prospects sont reçus de sources publicitaires.',
      sendLeadsSwitch: 'Envoyer les prospects des sites d\'annonces locatives via webhook',
      noPermission: 'Vous n\'avez pas la permission de modifier les paramètres du webhook',
      authKey: 'Clé d\'authentification',
      webhookName: 'Nom du webhook',
      endpoint: 'Point de terminaison',
      endpointHelp: 'Enregistrez votre webhook pour pouvoir capturer automatiquement les prospects des sites d\'annonces locatives dans le système de votre choix',
      propertiesHelp: 'Filtrer la livraison du webhook vers des propriétés spécifiques',
      allProperties: 'Toutes les propriétés',
      validateHelp: 'Avez-vous terminé la configuration ? Utilisez cet outil pour vérifier si votre configuration de webhook fonctionne correctement.',
      generateTestLead: 'Générer un prospect de test',
      sendTestLead: 'Envoyer un prospect de test',
      leadSent: 'Prospect envoyé !',
      testLeadWaitIframe: 'Si vous ne recevez pas de prospect de test pour la propriété sélectionnée dans les 15 prochaines minutes, veuillez communiquer avec le soutien à :',
      testLeadWait: 'Si vous ne recevez pas de prospect de test pour la propriété sélectionnée dans les 15 prochaines minutes,',
      contactSupport: 'veuillez communiquer avec le soutien',
      debugOnly: 'Ce bouton ne doit être utilisé qu\'à des fins de débogage.',
      enableLogging: 'Activer la journalisation (développeurs uniquement)',
      toast: {
        validationError: 'Erreur de validation',
        fieldsRequired: 'Les champs doivent être remplis.',
        settingsTitle: 'Paramètres d\'intégration',
        savedSuccess: 'Enregistré avec succès.',
        sentSuccess: 'Envoyé avec succès.',
      },
    },

    settings: {
      index: {
        general: 'Général',
        unsavedChanges: 'Vous avez des modifications non enregistrées.',
        settingsSaved: 'Paramètres de syndication enregistrés !',
        errorSaving: 'Erreur lors de l\'enregistrement des paramètres du client !',
      },
      routes: {
        generalTitle: 'Paramètres généraux',
        syndicationTitle: 'Paramètres de syndication',
        settingsTitle: 'Paramètres',
        updateConnectionPrefs: 'Mettre à jour les préférences de connexion',
        configureSpecific: 'Configurez des paramètres spécifiques pour vos sites d\'annonces locatives actifs',
      },
      common: {
        listingContent: 'Contenu de l\'annonce',
        unitAvailability: 'Disponibilité des logements',
        chooseUnitAvailability: 'Choisissez le paramètre de disponibilité des logements.',
        onlyAvailableUnits: 'Uniquement les logements disponibles',
        allUnits: 'Tous les logements',
        buildingNameOverride: 'Remplacement du nom du bâtiment',
        superAdmin: 'Super administrateur',
        pleaseNote: 'Veuillez noter : ',
        billingNotice: 'Cette décision influencera le nombre d\'annonces affichées sur {site}, ce qui pourrait affecter votre facturation.',
        yes: 'Oui',
        no: 'Non',
        select: 'Sélectionner',
        notAllowed: 'Non autorisé',
        allowed: 'Autorisé',
        siteId: 'Identifiant du site',
        omitBuildingDesc: 'Omettre la description du bâtiment',
        omitBuildingDescHelp: 'Cela ne peut se produire que lorsqu\'une description de logement est présente',
        petFriendlyDefault: 'Valeur par défaut Animaux acceptés',
        petFriendlyHelp: 'Peut être « Oui » ou « Non ». Si aucune politique concernant les animaux n\'est trouvée pour la propriété, ce sera la valeur envoyée à {target}.',
        smokingDefault: 'Valeur par défaut Fumeur / Non-fumeur',
        smokingHelp: 'Peut être « Autorisé » ou « Non autorisé ». Si aucune commodité concernant le tabac n\'est cochée pour la propriété, ce sera la valeur envoyée à {target}.',
        brokerageFeedDefault: 'Valeur par défaut du flux de courtage',
        brokerageFeedHelp: 'Peut être « Non (conserver dans les MITs) », « Toutes les propriétés » ou « Toutes les maisons unifamiliales ». Si aucun flux de courtage n\'est trouvé pour la propriété, ce sera la valeur envoyée à {target}.',
        brokerageKeepInMits: 'Non (conserver dans les MITs)',
        brokerageAllProperties: 'Toutes les propriétés',
        brokerageAllSingleFamily: 'Toutes les maisons unifamiliales',
        multiMessaging: 'Multi Messaging',
        exclude: 'Exclure',
        multiMessagingHelp: 'Cocher la case ci-dessus exclura vos propriétés de Multi Messaging sur les annonces Zumper.',
        digitalLeadConcierge: 'Digital Lead Concierge',
        optIn: 'Adhérer',
        digitalLeadConciergeHelp: 'Cocher la case ci-dessus inclura vos propriétés dans la fonctionnalité Digital Lead Concierge sur les annonces Zumper.',
      },
      general: {
        autoOnToggle: 'Bouton d\'activation automatique des sites d\'annonces locatives',
        autoOnHelp: 'Activez la fonctionnalité d\'activation automatique pour les sites d\'annonces locatives.',
        enableTrials: 'Activer les essais/offres gratuites',
        enableTrialsHelp1: 'Activez de nouvelles connexions gratuites et de nouveaux essais qui profiteraient aux propriétés de votre portefeuille.',
        enableTrialsHelp2: 'Vous garderez le contrôle sur les propriétés activées et désactivées à tout moment.',
        propertyAvailability: 'Disponibilité des propriétés',
        propertyAvailabilityHelp: 'Choisissez le filtre par défaut pour contrôler quelles propriétés sont affichées dans la section Syndication.',
        availabilityAll: 'Toutes les propriétés',
        availabilityAvailable: 'Uniquement les propriétés disponibles',
        availabilityNotAvailable: 'Propriétés non disponibles',
        urlTracking: 'Suivi des URL',
        urlTrackingHelp: 'Générateur d\'URL de campagne Google Analytics activé pour chaque site d\'annonces locatives dans la Syndication.',
        trafficSource: 'Source de trafic :',
        trafficSourceValue: 'Syndication',
        medium: 'Support',
        campaignName: 'Nom de la campagne',
        companyDescription: 'Description de l\'entreprise',
        companyDescriptionHelp: 'Fournissez une description/un paragraphe de présentation qui sera utilisé pour les sites d\'annonces locatives.',
        serviceOrder: 'Ordre des services',
        serviceOrderHelp: 'Choisissez l\'ordre d\'affichage de vos sites d\'annonces locatives.',
        propertyOverrides: 'Remplacements de propriété',
        legacySettings: 'Paramètres hérités (super-utilisateurs uniquement)',
      },
      propertyOverrides: {
        intro: 'Remplacez les éléments d\'annonce de l\'entreprise par les éléments d\'annonce de la propriété pour leurs annonces locatives respectives.',
        globalIndividualHelp: 'Activez toutes les propriétés pour qu\'elles utilisent les mêmes paramètres de remplacement <strong>globaux</strong>. Cela s\'appliquera aux propriétés qui n\'ont aucun paramètre de remplacement <strong>individuel</strong> défini.',
        globalFields: 'Champs de remplacement globaux de la propriété',
        individualHelp: 'Sélectionnez les paramètres <strong>individuels</strong> de la propriété. Si les paramètres <strong>individuels</strong> de la propriété diffèrent des paramètres <strong>globaux</strong>, les paramètres <strong>individuels</strong> de la propriété seront appliqués.',
        searchPlaceholder: 'Rechercher des propriétés...',
        overrideTooltip: 'Cette propriété possède des paramètres qui remplacent les paramètres globaux',
        takeEffect: 'Les paramètres de remplacement de propriété prennent effet sur ces connexions activées : {connections}',
        noEffect: 'Les paramètres de remplacement de propriété ne prendront effet sur aucune de vos connexions actuelles.',
        fields: {
          property: 'Propriété',
          address: 'Adresse',
          email: 'Courriel',
          logo: 'Logo',
          name: 'Nom',
          phone: 'Téléphone',
          site: 'Site',
        },
      },
      legacy: {
        intro: 'Consultez et gérez les paramètres hérités qui affectent vos annonces locatives.',
        banner: 'Les paramètres hérités peuvent affecter la façon dont vos annonces sont affichées sur diverses plateformes.',
        filterByPartner: 'Filtrer par partenaire :',
        allPartners: 'Tous les partenaires',
        removeFilter: 'Supprimer le filtre',
        addSetting: 'Ajouter un paramètre',
        editSetting: 'Modifier le paramètre',
        partner: 'Partenaire :',
        cannotChangePartner: 'Impossible de changer le partenaire pour les paramètres existants.',
        settingName: 'Nom du paramètre :',
        cannotChangeFieldKey: 'Impossible de changer field_key pour les paramètres existants.',
        selectPartnerFirst: 'Veuillez d\'abord sélectionner un partenaire.',
        noSettingsForPartner: 'Aucun paramètre disponible pour ce partenaire.',
        value: 'Valeur :',
        columns: {
          partner: 'Partenaire',
          fieldKey: 'Clé de champ',
          value: 'Valeur',
          description: 'Description',
          edit: 'Modifier',
          delete: 'Supprimer',
        },
        toast: {
          error: 'Erreur',
          clientNotSet: 'Le client actuel n\'est pas défini.',
          fetchFailed: 'Échec de la récupération des paramètres hérités',
          noDescription: 'Aucune description disponible.',
          idMissing: 'L\'identifiant du paramètre est manquant !',
          cannotDelete: 'Ce paramètre ne peut pas être supprimé car il est référencé par les mises en page d\'annonces. Veuillez d\'abord supprimer ces blocs de contenu.',
          cannotDeleteTitle: 'Impossible de supprimer le paramètre !',
          deleteTitle: 'Supprimer le paramètre',
          deleteConfirm: 'Êtes-vous sûr de vouloir supprimer le paramètre « {fieldKey} » pour le partenaire {partner} ? Cette action est irréversible.',
          deleted: 'Paramètre supprimé avec succès !',
          cannotDeleteDev: 'Ce paramètre ne peut pas être supprimé. Contactez le développement !',
          updated: 'Paramètre mis à jour avec succès !',
          updateFailed: 'Échec de la mise à jour du paramètre !',
          created: 'Paramètre créé avec succès !',
          createFailed: 'Échec de la création du paramètre !',
        },
      },
      dynamicControl: {
        jsonHelp: 'Saisissez un format JSON valide',
        currentFormat: 'Format actuel : {format}',
        invalidJson: 'Format JSON invalide',
      },
      adCreation: {
        title: 'Création d\'annonce',
        groupUnits: 'Regrouper les logements',
        chooseHowGenerated: 'Choisissez comment les annonces sont générées.',
        byBeds: 'Par nombre de chambres',
        byBedsBaths: 'Par nombre de chambres et salles de bain',
        byBedsBathsDen: 'Par chambres, salles de bain et bureau',
        byUnitName: 'Par nom de logement',
        showAllUnits: 'Afficher tous les logements',
      },
      adTitle: {
        title: 'Titre de l\'annonce',
        livePreview: 'Aperçu en direct',
        titleFormat: 'Format du titre',
        insertHelp: 'Vous pouvez insérer les caractères en gras suivants pour utiliser des données spécifiques à la propriété :',
        formatOption1: '[Nom du bâtiment] - [Nom du logement] [Type de bâtiment] à louer',
        formatOption2: '[Nom du logement] [Type de bâtiment] à louer - [Adresse du bâtiment]',
        formatOption3: '[Type de bâtiment] à louer : [Nom du logement] - [Nom du bâtiment]',
        formatCustom: 'Personnalisé (Avancé)',
        numBedrooms: 'Nombre de chambre',
        numBathrooms: 'Nombre de salles de bains',
        optBuildingName: 'Le nom du bâtiment',
        optOneLiner: 'Phrase d\'accroche pour promouvoir la propriété',
        optAddress: 'L\'adresse du bâtiment',
        optCityName: 'Nom de la ville',
        optProvinceName: 'Nom de la province',
        optUnitTypeName: 'Nom du type de logement',
        optBuildingType: 'Type de bâtiment',
        optNeighbourhood: 'Quartier',
      },
      tours: {
        title: 'Paramètres des visites',
        enableTours: 'Activer les visites',
        help: 'Les URL de visite peuvent être activées ou désactivées sur vos annonces. Cela n\'affectera que les propriétés ayant des visites actives.',
      },
      partners: {
        kijiji: {
          contactNote: 'Contactez Kijiji si vous n\'êtes pas certain du nombre d\'annonces incluses dans votre forfait.',
          showAmenities: 'Afficher les commodités',
          showUtilities: 'Afficher les services publics',
          petPolicyDefault: 'Politique par défaut sur les animaux',
          smokingDefault: 'Valeur par défaut sur le tabac',
          salesforceId: 'Identifiant Salesforce',
          salesforceHelp: 'Identifiant unique pour connecter votre compte Rentsync à Kijiji pour la syndication.',
          oneList: 'Une liste',
          categoryList: 'Liste par catégorie',
          hidePetFriendly: 'Masquer l\'affichage Animaux acceptés',
          selectDefault: 'Sélectionner une valeur par défaut',
          outdoorsOnly: 'Extérieur uniquement',
        },
        rentBoard: {
          siteIdHelp: 'Identifiant unique pour connecter votre compte Rentsync à Rent Board pour la syndication.',
          contactRentBoard: 'Contactez Rent Board',
          receivePackage: 'si vous n\'êtes pas certain du nombre d\'annonces incluses dans votre forfait.',
        },
        rentalsInfinity: {
          banner: 'Ces paramètres s\'appliqueront uniquement à tous les sites d\'annonces locatives admissibles du Réseau Infinity de Rentals.ca.',
          toursRequest: 'Demande de visite',
          toursLite: 'Visites Lite',
          toursLiteHelp: 'Activez les demandes de visite de base des prospects sur les sites d\'annonces locatives admissibles dans l\'ensemble de votre portefeuille',
          siteIdHelp: 'Identifiant unique pour connecter votre compte Rentsync pour la syndication, avec tous les sites d\'annonces locatives admissibles du Réseau Infinity de Rentals.ca.',
        },
        gottarent: {
          displayingPrice: 'Affichage du prix',
          choosePriceDisplay: 'Choisissez d\'afficher les prix ou d\'afficher « veuillez appeler » dans le tableau des logements.',
          showPrices: 'Afficher les prix',
          showPleaseCall: 'Afficher « veuillez appeler »',
        },
        usedCa: {
          userAccountId: 'Identifiant de compte utilisateur',
          userAccountHelp: 'L\'identifiant de compte utilisateur est utilisé pour associer les annonces à un compte Used.ca.',
        },
      },
    },
  },
} as const;
